import produce from "immer";
import { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { v4 as uuid } from "uuid";

import { useGetCustomFormValuesQuery } from "@/apollo/types";
import { SecondaryButton } from "@/components/elements/Button";
import { AppliedMultiSelect } from "@/components/elements/Select";
import { InputDescription } from "@/components/primitives/InputDescription";
import FieldLabel from "@/components/primitives/InputLabel";
import { DropdownOption, FormType } from "@/integrations";

import RedditAdsCustomReport from "./RedditAdsCustomReport";

type RedditAdsFormOption = DropdownOption<string> | string;

const normalizeOptions = (
  options: RedditAdsFormOption[] | undefined,
): DropdownOption<string>[] => {
  return (options ?? []).map((option) => {
    if (typeof option === "string") {
      return {
        label: option,
        value: option,
      };
    }
    return option;
  });
};

const useCustomFormFieldsQuery = (connectionId: string) => {
  const { data, loading, refetch } = useGetCustomFormValuesQuery({
    variables: {
      input: {
        connectionId,
        dependencies: "{}",
      },
    },
    onError() {
      return [];
    },
  });

  return { data: data?.getCustomFormValues.customFormValues, loading, refetch };
};

export type RedditAdsReport = {
  id: string;
  name: string;
  breakdowns: DropdownOption<string>[];
  fields: DropdownOption<string>[];
  timezone?: DropdownOption<string>;
};

export default function RedditAdsForm(props: {
  field: Extract<FormType, { type: "reddit-ads-form" }>;
}) {
  const context = useFormContext();
  const [selectedAdAccounts, setSelectedAdAccounts] = useState<
    DropdownOption<string>[] | undefined
  >(() => context.getValues()["selectedAdAccounts"]);
  const { data: customFieldsData } = useCustomFormFieldsQuery(
    context.getValues()["connectionId"],
  );

  const adAccountOptions = useMemo(
    () =>
      (props.field.selectedAdAccounts ?? []).map((account) => ({ ...account })),
    [props.field.selectedAdAccounts],
  );

  const breakdownOptions = useMemo(
    () =>
      normalizeOptions(customFieldsData?.breakdowns).length > 0
        ? normalizeOptions(customFieldsData?.breakdowns)
        : normalizeOptions(props.field.breakdowns),
    [customFieldsData?.breakdowns, props.field.breakdowns],
  );
  const fieldOptions = useMemo(
    () =>
      normalizeOptions(customFieldsData?.fields).length > 0
        ? normalizeOptions(customFieldsData?.fields)
        : normalizeOptions(props.field.fields),
    [customFieldsData?.fields, props.field.fields],
  );
  const timezoneOptions = useMemo(
    () =>
      normalizeOptions(customFieldsData?.timezones).length > 0
        ? normalizeOptions(customFieldsData?.timezones)
        : normalizeOptions(props.field.timezones),
    [customFieldsData?.timezones, props.field.timezones],
  );

  useEffect(() => {
    context.register("custom_reports", { required: false });
    context.register("selectedAdAccounts", { required: true });
  }, [context]);

  const getInitialReports = () => {
    const values = context.getValues()["custom_reports"];
    if (values === "NO_REPORTS") return [];
    return (values ?? [])
      .filter((v: RedditAdsReport) => !!v)
      .map((report: RedditAdsReport) => ({
        ...report,
        name: report.name ?? "",
      }));
  };

  const [reports, setReports] =
    useState<RedditAdsReport[]>(getInitialReports());

  const changeReport = (report: RedditAdsReport) => {
    setReports((prev) =>
      produce(prev, (draft) => {
        const cur = draft.find((rep) => rep.id === report.id);
        if (cur) {
          cur.name = report.name;
          cur.breakdowns = report.breakdowns;
          cur.fields = report.fields;
          cur.timezone = report.timezone;
        }
      }),
    );
  };

  const removeReport = (report: RedditAdsReport) => {
    setReports((prev) =>
      produce(prev, (draft) => {
        draft = draft.filter((rep) => rep.id !== report.id);
        return draft;
      }),
    );
  };

  const setValue = context.setValue;
  const setError = context.setError;

  const handleAdAccountChange = (values: DropdownOption<string>[]) => {
    setValue("selectedAdAccounts", values, {
      shouldValidate: true,
    });
    setSelectedAdAccounts(values);
  };

  useEffect(() => {
    if (reports.length === 0) {
      setValue("custom_reports", [], {
        shouldValidate: false,
      });
    }
  }, [reports.length, setValue]);

  useEffect(() => {
    const getReports = () => {
      if (!reports.length) {
        return [];
      }
      const foundReports = reports.every(
        (r) =>
          !!r.name &&
          r.breakdowns.length > 0 &&
          r.fields.length > 0 &&
          !!r.timezone,
      )
        ? reports
        : undefined;
      return foundReports;
    };

    const currentReports = getReports();
    if (currentReports && Array.isArray(currentReports)) {
      setValue("custom_reports", currentReports, {
        shouldValidate: true,
      });
    } else {
      setError("custom_reports", {
        message: "Validation error",
      });
    }
  }, [reports, setValue, setError]);

  return (
    <div className="space-y-8">
      <div>
        <FieldLabel id="selectedAdAccounts" required={true}>
          Ad Accounts
        </FieldLabel>
        <AppliedMultiSelect
          isMulti={true}
          isLoading={false}
          placeholder="Select one or more"
          onChange={handleAdAccountChange}
          options={adAccountOptions}
          value={selectedAdAccounts}
          hideSelectedOptions={false}
        />
        <InputDescription className="m-1">
          Choose the ad accounts from which Weld should retrieve data.
        </InputDescription>
      </div>
      {!!reports.length && (
        <div className="text-sm">Custom reports ({reports.length}/20)</div>
      )}
      {reports.map((report) => (
        <RedditAdsCustomReport
          key={report.id}
          report={report}
          onChangeReport={changeReport}
          onRemoveReport={removeReport}
          breakdownOptions={breakdownOptions}
          fieldOptions={fieldOptions}
          timezoneOptions={timezoneOptions}
          connectionId={context.getValues()["connectionId"]}
          selectedAdAccounts={selectedAdAccounts}
        />
      ))}
      <SecondaryButton
        onClick={() =>
          setReports((prev) => [
            ...prev,
            {
              id: uuid(),
              name: "",
              breakdowns: [],
              fields: [],
              timezone: undefined,
            },
          ])
        }
      >
        Add custom report
      </SecondaryButton>
    </div>
  );
}
